import {
  FormulaJson,
  FormulaValueJson,
  getFormulaKeyword,
  isColumnFormulaOperator,
  isRoundingFormulaOperator,
  isRowFormulaOperator,
} from '@doss/common-all'
import { Box, Typography } from '@mui/material'
import _, { isNil, orderBy } from 'lodash'
import { Fragment, useCallback } from 'react'
import { RxCross2 } from 'react-icons/rx'
import { Else, If, Then } from 'react-if'
import { FormulaOperator } from '~/common/generated/apollo-operations'
import useStyles from '~/common/hooks/useStyles'
import { ConditionExpressionRenderer } from '~/components/logic-expression/ConditionExpressionRenderer'
import { FormulaKeyword } from './FormulaKeyword'
import { FormulaValueExpression } from './FormulaValueExpression'

function getStyles() {
  return {
    expression: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      whiteSpace: 'nowrap',
    },
    multiplyIcon: {
      display: 'flex',
      fontSize: '12px',
    },
  }
}

interface FormulaExpressionProps {
  data: FormulaJson
}

export function FormulaExpression({ data }: FormulaExpressionProps) {
  const styles = useStyles(getStyles)

  const getOperator = useCallback(
    (operator: FormulaOperator) => {
      switch (operator) {
        case FormulaOperator.ADDITION:
          return '+'
        case FormulaOperator.SUBTRACTION:
          return '-'
        case FormulaOperator.MULTIPLICATION:
          return <RxCross2 style={styles.multiplyIcon} />
        case FormulaOperator.DIVISION:
          return '/'
        case FormulaOperator.SUM:
        case FormulaOperator.COUNT:
        case FormulaOperator.COUNT_DISTINCT:
        case FormulaOperator.AVERAGE:
        case FormulaOperator.SUMPRODUCT:
        case FormulaOperator.MIN:
        case FormulaOperator.MAX:
        case FormulaOperator.CEIL:
        case FormulaOperator.FLOOR:
        case FormulaOperator.ROUND:
        case FormulaOperator.EQUAL:
        case FormulaOperator.NOT_EQUAL:
        case FormulaOperator.GREATER_THAN:
        case FormulaOperator.GREATER_THAN_OR_EQUAL:
        case FormulaOperator.LESS_THAN:
        case FormulaOperator.LESS_THAN_OR_EQUAL:
        case FormulaOperator.CONTAINS:
        case FormulaOperator.DOES_NOT_CONTAIN:
        case FormulaOperator.CONTAINS_ANY:
        case FormulaOperator.CONTAINS_ALL:
        case FormulaOperator.DOES_NOT_CONTAIN_ANY:
        case FormulaOperator.IN:
        case FormulaOperator.NOT_IN:
        case FormulaOperator.IS_EMPTY:
        case FormulaOperator.IS_NOT_EMPTY:
        case FormulaOperator.AND:
        case FormulaOperator.OR:
        case FormulaOperator.NOT:
        case FormulaOperator.CONCAT:
        case FormulaOperator.LEFT:
        case FormulaOperator.RIGHT:
        case FormulaOperator.MID:
        case FormulaOperator.SUBSTITUTE:
        case FormulaOperator.LOWER:
        case FormulaOperator.UPPER:
        case FormulaOperator.TRIM:
        case FormulaOperator.LEN:
        case FormulaOperator.DATE:
        case FormulaOperator.DATEADD:
        case FormulaOperator.DATEDIFF:
        case FormulaOperator.DAY:
        case FormulaOperator.MONTH:
        case FormulaOperator.YEAR:
        case FormulaOperator.TO_TEXT:
        case FormulaOperator.TO_NUMBER:
        case FormulaOperator.TO_DATE:
          return operator
      }
    },
    [styles.multiplyIcon],
  )

  const sortedFormulaValues = orderBy(data.formulaValues, (value) => value.order, 'asc')
  let initialFormulaValue: FormulaValueJson | null
  if (!isColumnFormulaOperator(data.operator) && sortedFormulaValues.length > 0) {
    initialFormulaValue = sortedFormulaValues[0]
  } else {
    initialFormulaValue = null
  }

  const formulaValues = data.formulaValues.filter(
    (formulaValue) => formulaValue.id !== initialFormulaValue?.id,
  )
  const tableColumnFormulaValues = formulaValues.filter(
    (formulaValue) => !_.isNil(formulaValue.tableColumn),
  )

  const hasColumnOperationFilter = !isNil(data.columnOperationFilter)

  return (
    <Box sx={styles.expression}>
      <If condition={isColumnFormulaOperator(data.operator)}>
        <Then>
          <If condition={isRowFormulaOperator(data.operator)}>
            {/** For column and row level operators */}
            <Then>
              <Typography noWrap>
                <FormulaKeyword>
                  {getFormulaKeyword(data.operator, hasColumnOperationFilter)}
                </FormulaKeyword>
                (
              </Typography>
              {data.columnOperationFilter && (
                <>
                  <ConditionExpressionRenderer data={data.columnOperationFilter} />
                  <Typography>,</Typography>
                </>
              )}
              {data.formulaValues.map((formulaValue, idx) => (
                <>
                  <FormulaValueExpression data={formulaValue} />
                  {idx !== data.formulaValues.length - 1 && <Typography>,</Typography>}
                </>
              ))}
              <Typography>)</Typography>
            </Then>
            <Else>
              {/** For column-only level operators */}
              <Typography noWrap>
                <FormulaKeyword>
                  {getFormulaKeyword(data.operator, hasColumnOperationFilter)}
                </FormulaKeyword>
                (
              </Typography>
              {data.columnOperationFilter && (
                <>
                  <ConditionExpressionRenderer data={data.columnOperationFilter} />
                  <Typography>,</Typography>
                </>
              )}
              {tableColumnFormulaValues.map((formulaValue, idx) => (
                <>
                  <FormulaValueExpression data={formulaValue} />
                  {idx !== tableColumnFormulaValues.length - 1 && <Typography>,</Typography>}
                </>
              ))}
              <Typography>)</Typography>
            </Else>
          </If>
        </Then>
        <Else>
          <If condition={isRoundingFormulaOperator(data.operator)}>
            <Then>
              {/** For rounding level operators; ROUND may carry an optional digit count as a second value */}
              <Typography noWrap>
                <FormulaKeyword>{getFormulaKeyword(data.operator)}</FormulaKeyword>(
              </Typography>
              {sortedFormulaValues.map((formulaValue, idx) => (
                <Fragment key={formulaValue.id}>
                  <FormulaValueExpression data={formulaValue} />
                  {idx !== sortedFormulaValues.length - 1 && <Typography>,</Typography>}
                </Fragment>
              ))}
              <Typography>)</Typography>
            </Then>
            <Else>
              {/** For row-only level operators */}
              {!_.isNil(initialFormulaValue) && (
                <FormulaValueExpression data={initialFormulaValue} />
              )}
              {formulaValues.map((formulaValue) => (
                <>
                  <Typography noWrap>{getOperator(FormulaOperator[data.operator])}</Typography>
                  <FormulaValueExpression data={formulaValue} />
                </>
              ))}
            </Else>
          </If>
        </Else>
      </If>
    </Box>
  )
}
